// services/school-ui/src/pages/promotion/PromotionQueuePage.tsx
// Master-detail admin promotion-queue page. Left rail: QueueList.
// Right pane: PromotionDetail. Orchestrates selection state, filter
// state, and the pendingUndo flow.
//
// Backend: partner-owned promotion-svc. Auth handled at the gateway.
//
// Authorization: backend enforces admin-only on GET / approve /
// reject / preview / revert. This page renders a specific
// access-denied message on 403 so a non-admin who deep-links here
// gets a useful response (M3 review fix preserved from 1c13d33).

import { useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ConflictError,
  ForbiddenError,
  fetchPromotions,
  revert,
} from "./api";
import { PromotionDetail } from "./PromotionDetail";
import { QueueList } from "./QueueList";
import { UndoBanner } from "./UndoBanner";
import type { FilterValue, PendingUndo, Status } from "./types";
import { authHeaders } from "../../api/school";

export default function PromotionQueuePage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterValue>("pending");
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [pendingUndo, setPendingUndo] = useState<PendingUndo | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const {
    data: requests = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-promotions", filter],
    queryFn: () => fetchPromotions(filter),
    refetchInterval: filter === "pending" ? 15_000 : 60_000,
  });

  // Auto-select the first row when nothing's selected and the list arrives.
  useEffect(() => {
    if (!selectedRequestId && requests.length > 0) {
      setSelectedRequestId(requests[0]?.id ?? null);
    }
    if (selectedRequestId && !requests.find((r) => r.id === selectedRequestId)) {
      setSelectedRequestId(requests[0]?.id ?? null);
    }
  }, [requests, selectedRequestId]);

  const selected = requests.find((r) => r.id === selectedRequestId) ?? null;

  const revertMutation = useMutation({
    mutationFn: (id: string) => revert(id),
    onSuccess: (_data, id) => {
      setPendingUndo(null);
      setSelectedRequestId(id); // restore selection to the undone row
      void queryClient.invalidateQueries({ queryKey: ["admin-promotions"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-promotions-pending-count"] });
    },
    onError: (e) => {
      if (e instanceof ConflictError) {
        setToast(t("promotion.undo.failedReindex"));
      } else {
        setToast(t("promotion.undo.failedNetwork"));
      }
    },
  });

  function handleDecided(action: "approve" | "reject", _notes: string): void {
    if (!selected) return;
    setPendingUndo({
      requestId: selected.id,
      priorStatus: "pending" as Status,
      decidedAt: Date.now(),
      action,
    });
    if (filter === "pending") {
      const idx = requests.findIndex((r) => r.id === selected.id);
      const next = requests[idx + 1] ?? null;
      setSelectedRequestId(next?.id ?? null);
    }
  }

  if (error instanceof ForbiddenError) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="rounded border border-slate-200 bg-white p-6 max-w-md">
          <h3 className="text-sm font-semibold mb-2">
            {t("promotion.accessDenied.title")}
          </h3>
          <p className="text-slate-500 text-xs">{t("promotion.accessDenied.body")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col md:flex-row">
      <QueueList
        requests={requests}
        selectedRequestId={selectedRequestId}
        onSelect={setSelectedRequestId}
        filter={filter}
        onFilterChange={setFilter}
        onRefresh={() => {
          void refetch();
        }}
      />
      {isLoading ? (
        <div className="flex h-full flex-1 items-center justify-center text-sm text-slate-500">
          {t("promotion.preview.loading")}
        </div>
      ) : (
        <PromotionDetail
          request={selected}
          onDecided={handleDecided}
          onError={setToast}
        />
      )}
      {pendingUndo && (
        <UndoBanner
          key={pendingUndo.decidedAt}
          pending={pendingUndo}
          onUndo={() => revertMutation.mutate(pendingUndo.requestId)}
          onTimeout={() => setPendingUndo(null)}
        />
      )}
      {toast && (
        <div className="fixed bottom-4 left-4 z-50 rounded border border-red-200 bg-slate-50 px-4 py-2 text-xs shadow-lg">
          <span className="text-red-600">{toast}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-3 text-slate-500 hover:underline"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// usePromotionPendingCount — exported for the sidebar badge.
// Signature unchanged from the old page so the Sidebar continues to
// work without modification. Polled every 30s; cached under its own
// React Query key so it doesn't race with the page's filtered list.
// ──────────────────────────────────────────────────────────────────────
export function usePromotionPendingCount(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: ["admin-promotions-pending-count"],
    queryFn: async () => {
      const r = await fetch("/api/school/admin/promotions?status=pending", { headers: authHeaders() });
      if (!r.ok) return 0;
      const body = await r.json();
      return (body.requests ?? []).length as number;
    },
    refetchInterval: 30_000,
    enabled,
  });
}
